import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import PermissionErrorModal from 'components/modules/common/PermissionErrorModal';
import axios from 'axios';

// Tipo do estado do modal
interface PermissionModalState {
  isOpen: boolean;
  message: string;
  title?: string;
}

// Opções para retentativas no React Query
interface RetryOptions {
  queryKey: unknown[] | any;
  defaultMessage: string;
  onPermissionDenied?: () => void;
  showModal?: boolean; // Novo parâmetro opcional para controlar se a modal deve ser exibida
}

// Tipo do contexto
interface PermissionModalContextType {
  modalState: PermissionModalState;
  showPermissionModal: (message: string, title?: string) => void;
  hidePermissionModal: () => void;
  handlePermissionError: (error: any, defaultMessage: string) => boolean;
  getQueryRetryHandler: (options: RetryOptions) => (failureCount: number, error: Error) => boolean;
  getMutationErrorHandler: (defaultMessage: string, onError?: (error: any) => void) => (error: any) => void;
}

// Criar o contexto
const PermissionModalContext = createContext<PermissionModalContextType | undefined>(undefined);

// Props para o provider
interface PermissionModalProviderProps {
  children: ReactNode;
}

export const PermissionModalProvider: React.FC<PermissionModalProviderProps> = ({ children }) => {
  // Estado consolidado do modal
  const [modalState, setModalState] = useState<PermissionModalState>({
    isOpen: false,
    message: 'Você não possui as permissões necessárias para esta ação',
    title: 'Acesso Negado'
  });

  // Mostrar o modal de permissão
  const showPermissionModal = useCallback((message: string, title?: string) => {
    setModalState({
      isOpen: true,
      message,
      title: title || 'Acesso Negado'
    });
  }, []);

  // Esconder o modal de permissão
  const hidePermissionModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Função utilitária para tratar erros de permissão
  const handlePermissionError = useCallback((error: any, defaultMessage: string): boolean => {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      // Determinar a mensagem baseada na resposta ou usar a mensagem padrão
      const message = error.response.data?.message || defaultMessage;
      showPermissionModal(message);
      return false; // Não tentar novamente
    }
    return true; // Tentar novamente para outros tipos de erro
  }, [showPermissionModal]);

  // Helper para criar uma função de retry para o React Query
  const getQueryRetryHandler = useCallback(
    (options: RetryOptions) => (failureCount: number, error: Error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        // Só mostrar a modal se showModal não for explicitamente false
        if (options.showModal !== false) {
          const message = error.response.data?.message || options.defaultMessage;
          showPermissionModal(message);
        }
        
        // Chamar callback adicional se fornecido
        if (options.onPermissionDenied) {
          options.onPermissionDenied();
        }
        
        return false; // Não tentar novamente
      }
      return failureCount < 3; // Tentar novamente no máximo 3 vezes para outros erros
    },
    [showPermissionModal]
  );

  // Helper para criar um handler de erro para mutations
  const getMutationErrorHandler = useCallback(
    (defaultMessage: string, onError?: (error: any) => void) => (error: any) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        const message = error.response.data?.message || defaultMessage;
        showPermissionModal(message);
        return;
      }
      
      // Se tiver um handler de erro personalizado, chamá-lo
      if (onError) {
        onError(error);
      }
    },
    [showPermissionModal]
  );

  return (
    <PermissionModalContext.Provider 
      value={{ 
        modalState, 
        showPermissionModal, 
        hidePermissionModal, 
        handlePermissionError,
        getQueryRetryHandler,
        getMutationErrorHandler
      }}
    >
      {children}
      <PermissionErrorModal
        show={modalState.isOpen}
        onClose={hidePermissionModal}
        message={modalState.message}
        title={modalState.title}
      />
    </PermissionModalContext.Provider>
  );
};

// Hook personalizado para utilizar o contexto
export const usePermissionModal = (): PermissionModalContextType => {
  const context = useContext(PermissionModalContext);
  
  if (context === undefined) {
    throw new Error('usePermissionModal must be used within a PermissionModalProvider');
  }
  
  return context;
};
